import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Login.css';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(''); // Renamed from error to avoid conflict
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Step state for the two-step (email → password) login flow.
  // null            → user has not yet looked up their tenant.
  // { exists:true } → lookup hit; show tenant banner + password input.
  const [tenantInfo, setTenantInfo] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const passwordInputRef = useRef(null);
  const { login, isAuthenticated, loading, error: authError } = useAuth(); // Destructure auth error
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirectPath = location.state?.from?.pathname || '/dashboard';
      
      // Prevent redirect to login page
      if (redirectPath !== '/api/v1/auth/login') {
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate, location]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Step 1 of the two-step login: resolve which tenant the email belongs to.
  // Endpoint: POST /auth/lookup-tenant-by-email (public, rate-limited 10/15min/IP).
  const lookupTenant = async () => {
    setFormError('');
    setIsLookingUp(true);
    try {
      const res = await axios.post(
        '/auth/lookup-tenant-by-email',
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.data?.exists) {
        setTenantInfo({
          exists: true,
          statuscode: res.data.statuscode,
          statuslabel: res.data.statuslabel
        });
      } else {
        // Always-200 miss — backend deliberately avoids 404 to limit
        // status-based email enumeration. Surface a friendly message.
        setFormError("We don't recognise this email. Please check it or sign up for a new account.");
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 422) {
        setFormError('Please enter a valid email address.');
      } else if (status === 429) {
        setFormError('Too many lookup attempts. Please wait a few minutes and try again.');
      } else {
        setFormError(
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Could not look up tenant. Please try again.'
        );
      }
    } finally {
      setIsLookingUp(false);
    }
  };

  // Reset back to step 1 — user wants to type a different email.
  const handleChangeEmail = () => {
    setTenantInfo(null);
    setPassword('');
    setShowPassword(false);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Step 1 — no tenant resolved yet. Run the lookup.
    if (!tenantInfo?.exists) {
      await lookupTenant();
      return;
    }

    // Step 2 — tenant resolved, submit the password to the existing login.
    setFormError('');
    setIsSubmitting(true);
    try {
      const result = await login(email, password);
      if (!result.success) {
        setFormError(result.error);
      }
    } catch (err) {
      setFormError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Move focus to the password field as soon as the tenant resolves so
  // the user doesn't have to click into it.
  useEffect(() => {
    if (tenantInfo?.exists && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [tenantInfo]);

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Please enter your credentials to login</p>
        </div>
        
        {/* Session expired messages (both versions) */}
        {(location.state?.sessionExpired || authError === 'session-expired') && (
          <div className="session-expired-message">
            Your session has expired. Please login again.
          </div>
        )}
        
        {/* Form error messages */}
        {formError && !authError && (
          <div className="error-message">{formError}</div>
        )}
        
        {/* Auth error messages (non-session-expired) */}
        {authError && authError !== 'session-expired' && (
          <div className="error-message">{authError}</div>
        )}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              placeholder="Email address"
              readOnly={tenantInfo?.exists === true}
            />
          </div>

          {tenantInfo?.exists === true && (
            <div className="tenant-banner">
              <span>
                Logging in to <strong>{tenantInfo.statuslabel}</strong>
              </span>
              <button
                type="button"
                className="tenant-banner-change"
                onClick={handleChangeEmail}
              >
                Use a different email
              </button>
            </div>
          )}

          {tenantInfo?.exists === true && (
            <div className="form-group password-field">
              <input
                ref={passwordInputRef}
                className="form-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Password"
                minLength="8"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          )}

          {tenantInfo?.exists === true && (
            <div className="form-options">
              <div className="remember-me">
                <input type="checkbox" id="remember" />
                <label htmlFor="remember">Remember me</label>
              </div>
              <a href="/forgot-password" className="forgot-password">Forgot password?</a>
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting || isLookingUp}
          >
            {isSubmitting || isLookingUp ? (
              <LoadingSpinner size="small" />
            ) : tenantInfo?.exists === true ? 'Login' : 'Continue'}
          </button>
          
          <div className="login-divider">
            <span>or</span>
          </div>
          
          <div className="social-login">
            <button type="button" className="social-button google">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>
          
          {/* <p className="signup-link">
            Don't have an account? <a href="/registration">Sign up</a>
          </p> */}
        </form>
      </div>
    </div>
  );
}